import React, { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { signOut } from "firebase/auth";
import { auth } from "../../../firebaseConfig";
import { clearUser } from "../../redux/authSlice";
import API_URLS from "../apiUrls";
import { all_routes } from "../../../feature-module/router/all_routes";
import { useNavigate } from "react-router-dom";

type Props = {
  signInBtnClassName?: string;
};

const getInitials = (nameOrEmail?: string | null) => {
  if (!nameOrEmail) return "U";
  const base = nameOrEmail.includes("@") ? nameOrEmail.split("@")[0] : nameOrEmail;
  const parts = base.trim().split(/[.\s_-]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const AuthMenu: React.FC<Props> = ({ signInBtnClassName = "btn btn-white me-3" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routes = all_routes;

  // Single source of truth: Redux
  const fbUser = useSelector((state: any) => state.auth.user);
  const backendUser = useSelector((state: any) => state.auth.backendUser);
  const authLoading = useSelector((state: any) => state.auth.loading);

  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeTimer = useRef<number | null>(null);

  const logoutBackend = async () => {
    try {
      const current = auth.currentUser;
      if (!current) return;
      const idToken = await current.getIdToken(true);
      await fetch(API_URLS.AUTH.LOGOUT, {
        method: "POST",
        headers: { Authorization: `Bearer ${idToken}` },
      }).catch(() => { });
    } catch { }
  };

  const handleLogout = async () => {
    try {
      await logoutBackend();
      await signOut(auth);
    } finally {
      ["user", "user_id", "user_name"].forEach((k) => {
        sessionStorage.removeItem(k);
        localStorage.removeItem(k);
      });
      dispatch(clearUser());
      setOpen(false);
      window.location.href = routes.home1;
    }
  };

  const clearClose = () => {
    if (closeTimer.current) { window.clearTimeout(closeTimer.current); closeTimer.current = null; }
  };
  const scheduleClose = () => {
    clearClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 160) as unknown as number;
  };
  const openNow = () => { clearClose(); setOpen(true); };

  // While Firebase is still initializing, show nothing to avoid flash of Sign In button
  if (authLoading) return null;

  if (!fbUser) {
    return (
      <button
        type="button"
        className={signInBtnClassName}
        data-bs-toggle="modal"
        data-bs-target="#login-modal"
      >
        Sign In
      </button>
    );
  }

  const userName: string = backendUser?.name || fbUser?.displayName || fbUser?.email || "User";
  const userId: string | null = backendUser?.id != null ? String(backendUser.id) : null;
  const avatarSrc: string = fbUser?.photoURL || "";
  const initials = getInitials(userName);

  const isDark = () => document.documentElement.classList.contains("dark-mode");
  const cardBg = isDark() ? "#111317" : "#ffffff";
  const cardText = isDark() ? "#e7e9ee" : "#111317";
  const subtle = isDark() ? "#98a2b3" : "#667085";
  const borderCol = isDark() ? "rgba(255,255,255,0.08)" : "rgba(16,24,40,0.08)";

  return (
    <div
      ref={wrapperRef}
      className="position-relative"
      style={{ minWidth: 42, cursor: "pointer" }}
      onMouseEnter={openNow}
      onMouseLeave={scheduleClose}
      tabIndex={0}
      onFocus={openNow}
      onBlur={scheduleClose}
    >
      {avatarSrc ? (
        <img
          src={avatarSrc}
          alt="avatar"
          onClick={() => setOpen((o) => !o)}
          className="rounded-circle"
          style={{ width: 40, height: 40, objectFit: "cover", border: "2px solid var(--bs-body-bg, #fff)", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}
        />
      ) : (
        <div
          onClick={() => setOpen((o) => !o)}
          className="rounded-circle d-flex align-items-center justify-content-center"
          style={{ width: 40, height: 40, fontWeight: 700, userSelect: "none", background: "#e6e9f0", color: "#111317", border: "2px solid var(--bs-body-bg, #fff)", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}
          title={userName}
        >
          {initials}
        </div>
      )}

      <div
        ref={panelRef}
        className="shadow rounded-3"
        style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, minWidth: 240, zIndex: 1050, display: open ? "block" : "none", background: cardBg, color: cardText, border: `1px solid ${borderCol}` }}
        onMouseEnter={openNow}
        onMouseLeave={scheduleClose}
        tabIndex={0}
        onFocus={openNow}
        onBlur={scheduleClose}
        onMouseDown={(e) => e.preventDefault()}
        role="menu"
      >
        <div className="px-3 pt-3 pb-2">
          <div className="fw-semibold text-truncate" title={userName}>{userName}</div>
          {userId && <div className="small" style={{ color: subtle }}>ID: {userId}</div>}
        </div>
        <hr className="my-2" style={{ borderColor: borderCol, opacity: 1 }} />

        <button
          type="button"
          className="w-100 d-flex align-items-center gap-2 px-3 py-2 btn btn-link text-start text-decoration-none"
          style={{ color: cardText }}
          onClick={() => { setOpen(false); navigate(routes.userProfileSettings); }}
          role="menuitem"
        >
          <i className="isax isax-setting-2" />
          <span>Settings</span>
        </button>

        <button
          type="button"
          className="w-100 d-flex align-items-center gap-2 px-3 py-2 btn btn-link text-start text-decoration-none"
          style={{ color: "#d92d20" }}
          onClick={handleLogout}
          role="menuitem"
        >
          <i className="isax isax-logout" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
};

export default AuthMenu;
